import { Component, Event, EventEmitter, forceUpdate, h, Prop, State, Watch } from "@stencil/core";
import { i18nProvider } from "@trumpf/ux.i18n";
import {
    ChatServiceError,
    ErrorDispatcher,
    fetchChats,
    getCaseStatusString,
    IChatData,
    IError,
    NetworkError
} from "../../backend/chatService";
import { authService } from "../../utils/Auth";
import loadTranslations, { TranslationKey, TranslationOptions } from "../../utils/loadTranslations";
import { debug } from "../../utils/utils";

@Component({
    tag: "ai-chat-wrapper",
    styleUrl: "ai-chat-wrapper.scss",
    shadow: false
})
export class AiChatWrapper {
    private caseStatusInterval: NodeJS.Timeout | null = null;
    private errorListener: ((error: IError) => void) | null = null;
    private handleOnlineStatusChange!: () => void;
    private handleGlobalError!: (error: IError) => void;
    private handleResize!: () => void;
    private i18nSubscriberId: string;
    private authSubscription: (() => void) | null = null;

    /** The unique identifier of the case */
    @Prop() readonly caseId!: string;

    /** Authentication token for API requests */
    @Prop({ mutable: true }) token?: string;

    /** Current locale for translations */
    @Prop({ reflect: true, mutable: true }) locale: string;

    /** Enable debug logging */
    @Prop() readonly debug = false;

    @State() chat: IChatData | null = null;
    @State() caseStatus = "loading";
    @State() loading = false;
    @State() error: IError | null = null;
    @State() retryCount = 0;
    @State() isOffline = !navigator.onLine;
    @State() isMobile: boolean = (window as Window).innerWidth <= 768;
    @State() hasAcceptedDisclaimer = false;
    @State() translationsLoaded = false;

    /** Event emitted when the case status changes */
    @Event() caseStatusUpdated: EventEmitter<string>;

    constructor() {
        this.handleOnlineStatusChange = this.handleOnlineStatusChangeImpl.bind(this) as () => void;
        this.handleGlobalError = this.handleGlobalErrorImpl.bind(this) as (error: IError) => void;
        this.handleResize = this.handleResizeImpl.bind(this) as () => void;
        this.errorListener = this.handleGlobalError;
    }

    @Watch("error")
    handleErrorChange(newError: IError | null) {
        debug.log(this, "Error state changed:", newError);
    }

    private handleOnlineStatusChangeImpl() {
        this.isOffline = !navigator.onLine;
        if (this.isOffline) {
            debug.log(this, "Setting offline error");
            this.error = {
                errorCode: 503,
                errorMessage: i18nProvider.t("ai-in-service:error.offline")
            };
            this.loading = false;
        } else {
            debug.log(this, "Clearing error state");
            this.error = null;
            void this.retryFailedOperations();
        }
    }

    private handleGlobalErrorImpl(error: IError) {
        debug.log(this, "Setting global error:", error);
        this.error = error;
    }

    private handleResizeImpl() {
        this.isMobile = (window as Window).innerWidth <= 768;
    }

    async componentWillLoad() {
        // Get initial locale from i18nProvider
        const { language } = i18nProvider;
        this.locale = language;
        await this.loadTranslations();

        window.addEventListener("online", () => this.handleOnlineStatusChange());
        window.addEventListener("offline", () => this.handleOnlineStatusChange());
        window.addEventListener("resize", () => this.handleResize());
        ErrorDispatcher.addEventListener(this.errorListener);

        // Subscribe to auth state changes
        this.authSubscription = authService.subscribe(async (state) => {
            if (state.error) {
                this.error = {
                    errorCode: 401,
                    errorMessage: state.error
                };
            } else if (state.token) {
                this.token = state.token;
                if (this.error?.errorCode === 401) {
                    this.error = null;
                }
            }
        });

        if (!this.token) {
            try {
                this.token = await authService.getToken();
            } catch {
                // Error handling is done in the subscription
                return;
            }
        }

        await this.loadChat();

        // Auto-accept disclaimer if there are more than 2 messages (indicating user interaction)
        if (this.chat?.messages && this.chat.messages.length > 2) {
            this.hasAcceptedDisclaimer = true;
            debug.log(this, "Auto-accepted disclaimer due to user interaction");
        } else {
            this.hasAcceptedDisclaimer = false;
            debug.log(this, "Disclaimer needs explicit acceptance");
        }

        await this.loadCaseStatus();
        this.startCaseStatusSubscription();

        // Subscribe to i18n changes
        this.i18nSubscriberId = i18nProvider.subscribe(() => {
            this.translationsLoaded = true;
            forceUpdate(this);
        });
    }

    disconnectedCallback() {
        this.stopCaseStatusSubscription();
        window.removeEventListener("online", () => this.handleOnlineStatusChange());
        window.removeEventListener("offline", () => this.handleOnlineStatusChange());
        window.removeEventListener("resize", () => this.handleResize());
        if (this.errorListener) {
            ErrorDispatcher.removeEventListener(this.errorListener);
        }
        if (this.i18nSubscriberId) {
            i18nProvider.unsubscribe(this.i18nSubscriberId);
        }
        if (this.authSubscription) {
            this.authSubscription();
        }
    }

    @Watch("locale")
    async onLocaleChange() {
        const currentLanguage = i18nProvider.language;
        // Only update if the base language part is different
        const currentBase = currentLanguage.split("-")[0];
        const newBase = this.locale.split("-")[0];

        if (currentBase !== newBase) {
            await this.loadTranslations();
        }
    }

    private async loadTranslations() {
        try {
            const locale = await loadTranslations(this.locale);
            await i18nProvider.changeLanguage(locale);
            // Don't update this.locale here to prevent loops
            this.translationsLoaded = true;
        } catch (error) {
            console.error("Failed to load or change translations:", error);
        }
    }

    private async retryFailedOperations() {
        if (this.error) {
            this.error = null;
            this.retryCount++;
            try {
                await this.loadChat();
                await this.loadCaseStatus();
            } catch {
                // Error will be handled by the respective methods
            }
        }
    }

    private async loadChat() {
        debug.log(this, `Starting to load chat with caseId: ${this.caseId}`);

        if (!this.caseId) {
            console.error("[AiChatWrapper:loadChat] No caseId provided");
            this.error = {
                errorCode: 400,
                errorMessage: i18nProvider.t("ai-in-service:error.caseIdRequired")
            };
            return;
        }

        try {
            this.loading = true;
            debug.log(this, `Set loading to true, retrieving chats for caseId: ${this.caseId}`);

            // We need to modify the fetchChats function to accept the JWT token
            // For now, we'll pass the token as a parameter and update the backend implementation later
            const chats = await fetchChats(this.caseId);
            debug.log(this, `Received ${chats.length} chats for caseId: ${this.caseId}`);
            debug.log(this, "Chat data:", JSON.stringify(chats, null, 2));

            // Use the first chat or set to null if no chats are available
            this.chat = chats.length > 0 ? chats[0] : null;
            debug.log(
                this,
                `Selected chat:`,
                this.chat
                    ? `sessionId: ${this.chat.sessionId}, author: ${this.chat.author}, message count: ${this.chat.messages?.length || 0}`
                    : "No chat available"
            );

            // Auto-accept disclaimer if there are more than 2 messages (indicating user interaction)
            if (this.chat?.messages && this.chat.messages.length > 2) {
                this.hasAcceptedDisclaimer = true;
                debug.log(this, "Auto-accepted disclaimer due to user interaction");
            } else {
                this.hasAcceptedDisclaimer = false;
                debug.log(this, "Disclaimer needs explicit acceptance");
            }

            // Reset error state and retry count on successful load
            this.error = null;
            this.retryCount = 0;
        } catch (error) {
            console.error("[AiChatWrapper:loadChat] Error loading chat:", error);
            if (error instanceof NetworkError) {
                this.error = {
                    errorCode: error.code,
                    errorMessage: i18nProvider.t("ai-in-service:error.network")
                };
            } else if (error instanceof ChatServiceError) {
                this.error = {
                    errorCode: error.code,
                    errorMessage: error.message
                };
            } else {
                this.error = {
                    errorCode: 500,
                    errorMessage: i18nProvider.t("ai-in-service:error.unexpected")
                };
            }
            debug.log(this, `Set error state:`, this.error);
        } finally {
            this.loading = false;
            debug.log(this, "Set loading to false");
        }
    }

    private async loadCaseStatus() {
        if (!this.caseId) {
            console.warn(i18nProvider.t("ai-in-service:message.noCaseId"));
            return;
        }

        try {
            const newStatus = await getCaseStatusString(this.caseId);
            if (newStatus !== this.caseStatus) {
                this.caseStatus = newStatus;
            }
        } catch (error) {
            console.error("Error fetching case status:", error);
            // Don't update the main error state for status failures
            // Just update the status to indicate the error
            this.caseStatus = "error";
        }
    }

    private startCaseStatusSubscription() {
        if (!this.caseId) return;

        this.caseStatusInterval = setInterval(() => {
            if (!this.isOffline) {
                void this.loadCaseStatus();
            }
        }, 30000);
    }

    private stopCaseStatusSubscription() {
        if (this.caseStatusInterval) {
            clearInterval(this.caseStatusInterval);
            this.caseStatusInterval = null;
        }
    }

    private handleDisclaimerSubmitted() {
        this.hasAcceptedDisclaimer = true;
        // Initialize chat data if needed
        if (this.chat && !this.chat.messages) {
            this.chat = {
                ...this.chat,
                messages: []
            };
        }
    }

    render() {
        const t = (key: TranslationKey, options: TranslationOptions = { returnObjects: true }) =>
            i18nProvider.t(`ai-in-service:${String(key)}`, {
                ...options,
                interpolation: { escapeValue: false }
            });

        if (!this.translationsLoaded) {
            return (
                <div class="ai-chat-wrapper">
                    <div class="loading-state">{t("loading.translations")}</div>
                </div>
            );
        }

        if (this.loading) {
            return (
                <div class="ai-chat-wrapper">
                    <div class="loading-state">
                        <ux-loader variant="horizontal"></ux-loader>
                    </div>
                </div>
            );
        }

        const defaultChat: IChatData = {
            sessionId: `s-${Date.now()}`,
            author: "User",
            group: "User",
            timestamp: new Date().toISOString(),
            messages: []
        };

        // Get disclaimer content and ensure it's an array
        const disclaimerContent = (() => {
            const content = t("legalDisclaimer.content", { returnObjects: true });
            return Array.isArray(content) ? content : [content];
        })();

        // Brief debug log
        debug.log(this, "Message count:", this.chat?.messages?.length);
        debug.log(this, "hasAcceptedDisclaimer:", this.hasAcceptedDisclaimer);

        // Show disclaimer if 2 or fewer messages and hasn't been accepted yet
        const showDisclaimer = this.chat?.messages && this.chat.messages.length <= 2 && !this.hasAcceptedDisclaimer;
        debug.log(this, "showDisclaimer:", showDisclaimer);

        return (
            <div class="ai-chat-wrapper">
                <div class="ai-chat-container">
                    <ai-chat
                        class={showDisclaimer ? "disabled-chat" : ""}
                        token={this.token}
                        data={this.chat || defaultChat}
                        caseId={this.caseId}
                        caseStatus={this.caseStatus}
                        locale={this.locale}
                        error={this.error}
                        debug={this.debug}
                    ></ai-chat>
                    {showDisclaimer && (
                        <div class="disclaimer-overlay">
                            <legal-disclaimer
                                headline={t("legalDisclaimer.headline")}
                                button-label={t("legalDisclaimer.buttonLabel")}
                                checkbox-label={t("legalDisclaimer.checkboxLabel")}
                                onDisclaimerSubmitted={() => this.handleDisclaimerSubmitted()}
                            >
                                {disclaimerContent.map((content, index) => (
                                    <ux-copy variant="default" key={index}>
                                        {index === 1 ? <strong>{content}</strong> : content}
                                    </ux-copy>
                                ))}
                            </legal-disclaimer>
                        </div>
                    )}
                </div>
            </div>
        );
    }
}
